// ./src/components/generic/form/DateTimeField.tsx
//
// One field for picking a date (optionally with a time) on the shared
// `(name, value)` change contract. It wraps Mantine's DateTimePicker /
// DatePickerInput today, but owning it here means we can add behaviour
// (presets, "now", relative hints, validation) in one place.
//
// Values are plain strings: "YYYY-MM-DD HH:mm:ss" with a time, "YYYY-MM-DD"
// when `dateOnly`. Display is LOCALE-AWARE with a month name ("Jul 14, 2026" in
// the US, "14 Jul 2026" in Europe) and the 12h/24h clock follows the locale too.

import React, { useMemo } from 'react';
import { DateTimePicker, DatePickerInput } from '@mantine/dates';

import { clockFormat, isAfterCeiling, pickerCeiling, pickerFloor, todayIn } from '@em/utils';
import classes from './DateTimeField.module.css';
import { DATE_PART, isPastDateTime, markedDayProps, type MarkedDays } from './dateTimeFieldRules';
import { FieldChangeHandler } from './types';
import { useResponsiveDatePickerProps } from './useResponsiveDatePicker';

// The field's pure rules live in `dateTimeFieldRules.ts` (no React, no CSS — the guards run them); re-exported here.
export { formatMarkedDays, formatPickerMoment, isPastDateTime, markedDayProps, type MarkedDays } from './dateTimeFieldRules';

type MantineSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export interface DateTimeFieldProps {
  name?: string;
  value?: string | null;
  onChange?: FieldChangeHandler<string | null>;
  label?: React.ReactNode;
  description?: React.ReactNode;
  placeholder?: string;
  /** Date only (no time) — e.g. all-day events / deadlines. */
  dateOnly?: boolean;
  minDate?: string;
  maxDate?: string;
  /**
   * The earliest value this field may take — the paired START, for an end field (owner, 2026-09-30: "the date/time
   * pickers should not let set end date/time before start time"). With a time, the picker clamps the TIME on that day
   * too (Mantine clamps to a full `minDate`); date-only, the day. Wins over `minDate` / `disallowPast` when later.
   */
  notBefore?: string | null;
  /**
   * ROADMAP #137 — the latest value this field may take — a paired END (an event's end above its RSVP deadline). With a
   * time, the picker clamps the TIME on that day too; date-only, the day. Wins over `maxDate` when earlier. A value past
   * it (a stored one, after the event was moved earlier; or a day just picked, at `defaultTime`) shows
   * `notAfterErrorText` under the field — and Mantine CLAMPS it to the ceiling when the picker closes (#137 review: it
   * does, in both the popover and the phone's modal), so opening the picker over a late value fixes it.
   */
  notAfter?: string | null;
  /** Shown when the value is after `notAfter`. */
  notAfterErrorText?: string;
  /**
   * The time ("HH:mm:ss") a newly picked DAY gets (Mantine's `defaultTimeValue`; with a time only). E.g. a deadline: a
   * host who picks only a day means "through that day", not its first minute.
   */
  defaultTime?: string;
  clearable?: boolean;
  required?: boolean;
  disabled?: boolean;
  size?: MantineSize;
  leftSection?: React.ReactNode;
  style?: React.CSSProperties;
  error?: React.ReactNode;
  /**
   * ROADMAP A2e #94 — days to MARK in the calendar (a ring in the accent colour), e.g. the event's day(s) under its
   * deadline picker. Marking only: whether a day can be picked is still `minDate` / `maxDate`. Say what the mark means
   * in `description` (`formatMarkedDays`).
   */
  markDays?: MarkedDays | null;
  /** Width passthrough (Mantine style prop). */
  w?: React.CSSProperties['width'];
  /** Max-width passthrough (Mantine style prop). */
  maw?: React.CSSProperties['maxWidth'];
  /**
   * Disallow a past date/time: floors the calendar at today AND shows an inline
   * error when the value is in the past — without snapping (which fought typing
   * the minutes). Callers should also block submit (see `isPastDateTime`).
   */
  disallowPast?: boolean;
  /** Message shown when a past value is entered and `disallowPast`. */
  pastErrorText?: string;
  /**
   * The zone the value is a wall clock IN (an event's own time zone — the event editors read and save their pickers
   * there). Only "is it in the past" depends on it. Omitted → the browser's.
   */
  timeZone?: string | null;
}

export const DateTimeField: React.FC<DateTimeFieldProps> = ({
  name,
  value,
  onChange,
  dateOnly = false,
  minDate,
  maxDate,
  notBefore,
  notAfter,
  notAfterErrorText = 'Too late — pick an earlier time',
  defaultTime,
  disallowPast = false,
  error,
  pastErrorText = 'Can’t be in the past',
  timeZone,
  markDays,
  ...rest
}) => {
  const timeFormat = useMemo(() => clockFormat(), []);
  // On phones the calendar opens as a centered modal (see the hook) so it's easy
  // to use and — for date+time — stays open long enough to pick the time.
  const responsive = useResponsiveDatePickerProps();

  const timePart = timeFormat === '12h' ? 'h:mm A' : 'HH:mm';
  const valueFormat = dateOnly ? DATE_PART : `${DATE_PART} ${timePart}`;

  // The paired start raises the floor (`pickerFloor` — pure, in @em/utils, so the guard runs it).
  // ⚠ "Today" in the value's zone (an event's), not this device's — a Tokyo host could not pick tonight's LA date.
  const effectiveMinDate = pickerFloor({ minDate, notBefore, dateOnly, disallowPast, today: todayIn(timeZone) });

  // #137 — the paired end lowers the ceiling (`pickerCeiling`, pure in @em/utils).
  const effectiveMaxDate = pickerCeiling({ maxDate, notAfter, dateOnly });

  // Inform (don't snap): flag a past value, or one after the ceiling, so the user can fix it.
  const pastError = disallowPast && isPastDateTime(value, dateOnly, timeZone) ? pastErrorText : undefined;
  const afterError = notAfter && isAfterCeiling(value, effectiveMaxDate, dateOnly) ? notAfterErrorText : undefined;
  const effError = error ?? pastError ?? afterError;

  const handleChange = (v: string | null) => onChange?.(name, v);

  const getDayProps = markDays?.from ? (day: string) => markedDayProps(day, markDays, classes.markedDay) : undefined;

  // Keep the formatted value on one line (it can be long in narrow columns).
  const nowrap = { input: { whiteSpace: 'nowrap' as const, overflow: 'hidden', textOverflow: 'ellipsis' } };

  if (dateOnly) {
    return (
      <DatePickerInput
        value={value ?? null}
        onChange={handleChange}
        minDate={effectiveMinDate}
        maxDate={effectiveMaxDate}
        valueFormat={valueFormat}
        error={effError}
        styles={nowrap}
        getDayProps={getDayProps}
        {...responsive}
        {...rest}
      />
    );
  }
  return (
    <DateTimePicker
      value={value ?? null}
      onChange={handleChange}
      minDate={effectiveMinDate}
      maxDate={effectiveMaxDate}
      // ⚠ Only for an EMPTY field: Mantine seeds its time box from `defaultTimeValue` before the value's own time, so a
      // 6:00 PM deadline showed 11:59 PM (and a new day picked kept it).
      defaultTimeValue={value ? undefined : defaultTime}
      valueFormat={valueFormat}
      timePickerProps={{ format: timeFormat }}
      error={effError}
      styles={nowrap}
      getDayProps={getDayProps}
      {...responsive}
      {...rest}
    />
  );
};
